import React from "react";
import { BrowserRouter as Router, Redirect, Switch, Route, useLocation } from "react-router-dom";

import Login from "screens/Authentication/Login";
import CompleteProfile from "screens/Authentication/CompleteProfile";
import ChangePassword from "screens/Authentication/ChangePassword";
import ChooseArea from "screens/Authentication/ChooseArea";
import Register from "screens/Authentication/Register";
import Forgot from "screens/Authentication/Forgot";
import CreatePassword from "screens/Authentication/CreatePassword";
import Approval from "screens/Approval";
import BrazilNeuromodulationEvent from "screens/BrazilNeuromodulationEvent";
import PrivacyPolicy from "screens/PrivacyPolicy";

import DashboardHome from "screens/AuthenticatedArea/Dashboard/Home";
import DashboardMe from "screens/AuthenticatedArea/Dashboard/Me";
import CourseDetails from "screens/AuthenticatedArea/CourseDetails";
import Favorites from "screens/AuthenticatedArea/Favorites";
import SubCategory from "screens/AuthenticatedArea/SubCategory";
import SupportVideoDetails from "screens/AuthenticatedArea/SupportVideoDetails";
import Faq from "screens/AuthenticatedArea/Faq";

import MyAccount from "screens/AuthenticatedArea/MyAccount";
import Support from "screens/AuthenticatedArea/Support";
import SupportDetails from "screens/AuthenticatedArea/SupportDetails";
import Live from "screens/AuthenticatedArea/Live";
import ContactUs from "screens/AuthenticatedArea/ContactUs";
import ChatAgent from "screens/AuthenticatedArea/ChatAgent";
import SubCategoryFiltred from "screens/AuthenticatedArea/SubCategoryFiltred";
import Quiz from "screens/AuthenticatedArea/Quiz";
import SubCategoryShare from "screens/AuthenticatedArea/SubCategoryShare";
import NotFound from "screens/NotFound";
import AuthenticatedLayout from "Layout/AuthenticatedLayout";

import ImageQualifier from "screens/AuthenticatedArea/ImageQualifier";
import AnalyticsListener from "components/AnalyticsListener";
import MVUEssentialsHome from "screens/MVUEssentialsArea/Home";
import MVUEssentialsRegister from "screens/MVUEssentialsArea/Authentication/Register";
import MVUEssentialsCourseDetails from "screens/MVUEssentialsArea/MVUEssentialsCourseDetails";
import MVUEssentialsSubCategory from "screens/MVUEssentialsArea/MVUEssentialsSubCategory";
import MVUEssentialsForgot from "screens/MVUEssentialsArea/Authentication/Forgot";
import MVUEssentialsCreatePassword from "screens/MVUEssentialsArea/Authentication/CreatePassword";
import MVUAcceleratorHome from "screens/MVUAcceleratorArea/Home";
import MVUAcceleratorRegister from "screens/MVUAcceleratorArea/Authentication/Register";
import MVUAcceleratorCourseDetails from "screens/MVUAcceleratorArea/MVUAcceleratorCourseDetails";
import MVUAcceleratorSubCategory from "screens/MVUAcceleratorArea/MVUAcceleratorSubCategory";
import MVUAcceleratorForgot from "screens/MVUAcceleratorArea/Authentication/Forgot";
import MVUAcceleratorCreatePassword from "screens/MVUAcceleratorArea/Authentication/CreatePassword";
import LearningPathsList from "screens/AuthenticatedArea/LearningPaths/List";
import LearningPathDetails from "screens/AuthenticatedArea/LearningPaths/Details";
import Notifications from "screens/AuthenticatedArea/Notifications";
import NotificationDetails from "screens/AuthenticatedArea/NotificationDetails";
import StaffEssentialsInvites from "screens/AuthenticatedArea/StaffEssentialsInvites";
import Survey from "screens/AuthenticatedArea/Survey";
import UserApprovals from "screens/AuthenticatedArea/UserApprovals";
import AccessProfiles from "screens/AuthenticatedArea/AccessProfiles";
import TrainingApprovals from "screens/AuthenticatedArea/TrainingApprovals";
import TrainingApprovalReview from "screens/AuthenticatedArea/TrainingApprovalReview";
import MyCertificates from "screens/AuthenticatedArea/MyCertificates";
import CompetitiveIntelligence from "screens/AuthenticatedArea/CompetitiveIntelligence";
import CompetitiveIntelligenceResearch from "screens/AuthenticatedArea/CompetitiveIntelligenceResearch";
import CompetitiveIntelligenceResearchDetail from "screens/AuthenticatedArea/CompetitiveIntelligenceResearchDetail";
import CompetitiveIntelligenceMarket from "screens/AuthenticatedArea/CompetitiveIntelligenceMarket";
import CompetitiveIntelligenceAssistants from "screens/AuthenticatedArea/CompetitiveIntelligenceAssistants";
import Analytics from "screens/AuthenticatedArea/Analytics";
import { COMPETITIVE_INTELLIGENCE_DEFAULT_PATH, UNIVERSE_AUTHENTICATED_PATHS } from "./paths";
import { LEGACY_LITE_BASE_PATH, LITE_BASE_PATH, replaceLegacyLitePath } from "utils/portal";

function LegacyLiteRouteRedirect() {
  const location = useLocation();

  return (
    <Redirect to={{
      pathname: replaceLegacyLitePath(location.pathname),
      search: location.search,
      hash: location.hash,
    }} />
  );
}

export default function AppRouter() {

  return (
    <Router>
      <AnalyticsListener />

      <Switch>
        <Route path="/change-password" exact><ChangePassword area="universe" /></Route>
        <Route path="/mvu-lite/change-password" exact><ChangePassword area="lite" /></Route>
        <Route path="/mvu-accelerator/change-password" exact><ChangePassword area="accelerator" /></Route>
        <Route path="/complete-profile" exact><CompleteProfile area="universe" /></Route>
        <Route path="/mvu-lite/complete-profile" exact><CompleteProfile area="lite" /></Route>
        <Route path="/mvu-accelerator/complete-profile" exact><CompleteProfile area="accelerator" /></Route>
        {/* PUBLIC */}
        <Route path="/" exact><Login /></Route>
        <Route path="/login" exact><Login /></Route>
        <Route path="/choose-area" exact><ChooseArea /></Route>
        <Route path="/register" exact><Register /></Route>
        <Route path="/forgot" exact><Forgot /></Route>
        <Route path="/create-password" exact><CreatePassword /></Route>
        <Route path="/brazil-neuromodulation-event" exact><BrazilNeuromodulationEvent /></Route>
        <Route path="/privacy-policy" exact><PrivacyPolicy /></Route>
        <Route path="/approval/:token" exact><Approval /></Route>
        <Route path="/share/:token" exact><SubCategoryShare /></Route>
        {/* Lite portal; distributed /mvu-essentials links redirect to the canonical /mvu-lite path. */}
        <Route path={LEGACY_LITE_BASE_PATH}><LegacyLiteRouteRedirect /></Route>
        <Route path={`${LITE_BASE_PATH}/login`} exact><Redirect to="/login" /></Route>
        <Route path={`${LITE_BASE_PATH}/forgot`} exact><MVUEssentialsForgot /></Route>
        <Route path={`${LITE_BASE_PATH}/create-password`} exact><MVUEssentialsCreatePassword /></Route>
        <Route path={`${LITE_BASE_PATH}/register/:code`} exact><MVUEssentialsRegister /></Route>
        <Route
          path={[
            LITE_BASE_PATH,
            `${LITE_BASE_PATH}/trails`,
            `${LITE_BASE_PATH}/trails/:id`,
            `${LITE_BASE_PATH}/approvals`,
            `${LITE_BASE_PATH}/access`,
            `${LITE_BASE_PATH}/training-approvals`,
            `${LITE_BASE_PATH}/training-approvals/:id`,
            `${LITE_BASE_PATH}/notifications`,
            `${LITE_BASE_PATH}/notifications/:id`,
            `${LITE_BASE_PATH}/certificates`,
            `${LITE_BASE_PATH}/quiz/:id`,
            `${LITE_BASE_PATH}/survey/:courseId`,
            `${LITE_BASE_PATH}/details/:type`,
            `${LITE_BASE_PATH}/details/:type/:id`,
            `${LITE_BASE_PATH}/:type/:id`,
          ]}
        >
          <AuthenticatedLayout portal="essentials">
            <Switch>
              <Route path={LITE_BASE_PATH} exact><MVUEssentialsHome /></Route>
              <Route path={`${LITE_BASE_PATH}/trails`} exact><LearningPathsList portal="essentials" /></Route>
              <Route path={`${LITE_BASE_PATH}/trails/:id`} exact><LearningPathDetails portal="essentials" /></Route>
              <Route path={`${LITE_BASE_PATH}/approvals`} exact><UserApprovals /></Route>
              <Route path={`${LITE_BASE_PATH}/access`} exact><AccessProfiles /></Route>
              <Route path={`${LITE_BASE_PATH}/training-approvals`} exact><TrainingApprovals /></Route>
              <Route path={`${LITE_BASE_PATH}/training-approvals/:id`} exact><TrainingApprovalReview /></Route>
              <Route path={`${LITE_BASE_PATH}/notifications`} exact><Notifications /></Route>
              <Route path={`${LITE_BASE_PATH}/notifications/:id`} exact><NotificationDetails /></Route>
              <Route path={`${LITE_BASE_PATH}/certificates`} exact><MyCertificates /></Route>
              <Route path={`${LITE_BASE_PATH}/quiz/:id`} exact><Quiz /></Route>
              <Route path={`${LITE_BASE_PATH}/survey/:courseId`} exact><Survey /></Route>
              <Route path={`${LITE_BASE_PATH}/details/:type/:id`} exact><MVUEssentialsSubCategory /></Route>
              <Route path={`${LITE_BASE_PATH}/details/:type`} exact><MVUEssentialsSubCategory /></Route>
              <Route path={`${LITE_BASE_PATH}/:type/:id`} exact><MVUEssentialsCourseDetails /></Route>
              <Route path="*"><NotFound /></Route>
            </Switch>
          </AuthenticatedLayout>
        </Route>
        {/* MVU Accelerator */}
        <Route path="/mvu-accelerator/login" exact><Redirect to="/login" /></Route>
        <Route path="/mvu-accelerator/forgot" exact><MVUAcceleratorForgot /></Route>
        <Route path="/mvu-accelerator/create-password" exact><MVUAcceleratorCreatePassword /></Route>
        <Route path="/mvu-accelerator/register/:code" exact><MVUAcceleratorRegister /></Route>
        <Route path={["/mvu-accelerator/access", "/mvu-accelerator/myaccount", "/mvu-accelerator/approvals", "/mvu-accelerator/training-approvals", "/mvu-accelerator/training-approvals/:id", "/mvu-accelerator/notifications", "/mvu-accelerator/notifications/:id", "/mvu-accelerator/certificates", "/mvu-accelerator/quiz/:id", "/mvu-accelerator/survey/:courseId",]}>
          <AuthenticatedLayout portal="accelerator">
            <Switch>
              <Route path="/mvu-accelerator/myaccount" exact><MyAccount /></Route>
              <Route path="/mvu-accelerator/access" exact><AccessProfiles /></Route>
              <Route path="/mvu-accelerator/approvals" exact><UserApprovals /></Route>
              <Route path="/mvu-accelerator/training-approvals" exact><Redirect to="/mvu-accelerator" /></Route>
              <Route path="/mvu-accelerator/training-approvals/:id" exact><Redirect to="/mvu-accelerator" /></Route>
              <Route path="/mvu-accelerator/notifications" exact><Notifications /></Route>
              <Route path="/mvu-accelerator/notifications/:id" exact><NotificationDetails /></Route>
              <Route path="/mvu-accelerator/certificates" exact><Redirect to="/mvu-accelerator" /></Route>
              <Route path="/mvu-accelerator/quiz/:id" exact><Quiz /></Route>
              <Route path="/mvu-accelerator/survey/:courseId" exact><Survey /></Route>
              <Route path="*"><NotFound /></Route>
            </Switch>
          </AuthenticatedLayout>
        </Route>
        <Route
          path={[
            "/mvu-accelerator",
            "/mvu-accelerator/trails",
            "/mvu-accelerator/trails/:id",
            "/mvu-accelerator/details/:type",
            "/mvu-accelerator/details/:type/:id",
            "/mvu-accelerator/:type/:id",
          ]}
        >
          <AuthenticatedLayout portal="accelerator">
            <Switch>
              <Route path="/mvu-accelerator" exact><MVUAcceleratorHome /></Route>
              <Route path="/mvu-accelerator/trails" exact><Redirect to="/mvu-accelerator" /></Route>
              <Route path="/mvu-accelerator/trails/:id" exact><Redirect to="/mvu-accelerator" /></Route>
              <Route path="/mvu-accelerator/details/:type/:id" exact><MVUAcceleratorSubCategory /></Route>
              <Route path="/mvu-accelerator/details/:type" exact><MVUAcceleratorSubCategory /></Route>
              <Route path="/mvu-accelerator/:type/:id" exact><MVUAcceleratorCourseDetails /></Route>
              <Route path="*"><NotFound /></Route>
            </Switch>
          </AuthenticatedLayout>
        </Route>
        {/* MVU Universe */}
        <Route
          path={UNIVERSE_AUTHENTICATED_PATHS}
        >
          <AuthenticatedLayout>
            <Switch>
              <Route path="/image-qualifier" exact><ImageQualifier /></Route>

              <Route path="/dashboard" exact><DashboardHome /></Route>

              <Route path="/dashboard/me" exact><DashboardMe /></Route>

              <Route path="/trails" exact><LearningPathsList /></Route>

              <Route path="/trails/:id" exact><LearningPathDetails /></Route>

              <Route path="/favorites" exact><Favorites /></Route>

              <Route path="/notifications" exact><Notifications /></Route>

              <Route path="/notifications/:id" exact><NotificationDetails /></Route>

              <Route path="/approvals" exact><UserApprovals /></Route>
              <Route path="/access" exact><AccessProfiles /></Route>
              <Route path="/training-approvals" exact><TrainingApprovals /></Route>
              <Route path="/training-approvals/:id" exact><TrainingApprovalReview /></Route>
              <Route path="/accelerator-certificate-approvals" exact><Redirect to="/dashboard" /></Route>
              <Route path="/certificates" exact><MyCertificates /></Route>
              <Route
                path="/competitive-intelligence"
                exact
                render={({ location }) => (
                  <Redirect to={{ ...location, pathname: COMPETITIVE_INTELLIGENCE_DEFAULT_PATH }} />
                )}
              />
              <Route path="/competitive-intelligence/research" exact><CompetitiveIntelligenceResearch /></Route>
              <Route path="/competitive-intelligence/research/:id" exact><CompetitiveIntelligenceResearchDetail /></Route>
              <Route path="/competitive-intelligence/market" exact><CompetitiveIntelligenceMarket /></Route>
              <Route path="/competitive-intelligence/assistants" exact><CompetitiveIntelligenceAssistants /></Route>
              <Route path="/competitive-intelligence/overview" exact><CompetitiveIntelligence /></Route>
              <Route path="/analytics" exact><Analytics /></Route>

              <Route path="/faq" exact><Faq /></Route>

              <Route path="/contact" exact><ContactUs /></Route>

              <Route path="/live/:id" exact><Live /></Route>

              <Route path="/support" exact><Support /></Route>

              <Route path="/support/:id" exact><SupportDetails /></Route>

              <Route path="/myaccount" exact><MyAccount /></Route>

              <Route path="/quiz/:id" exact><Quiz /></Route>

              <Route path="/survey/:courseId" exact><Survey /></Route>

              <Route path="/search" exact><SubCategoryFiltred /></Route>

              <Route path="/sub-category/:categoryId/service-supports/:contentId" exact><SupportVideoDetails /></Route>

              <Route path="/sub-category/:type" exact><SubCategory /></Route>

              <Route path="/sub-category/:id/:type" exact><SubCategory /></Route>

              <Route path="/mode/:type/:id" exact><CourseDetails /></Route>

              <Route path="/mode/:type/:id/:lesson" exact><CourseDetails /></Route>

              <Route path="/chat-agent" exact><ChatAgent /></Route>

              <Route path="/staff/essentials-invites" exact><StaffEssentialsInvites /></Route>

              <Route path="*"><NotFound /></Route>
            </Switch>
          </AuthenticatedLayout>
        </Route>

        <Route path="*">
          <NotFound />
        </Route>
      </Switch>
    </Router>
  );
}
